/*
 * Chunk-load failure → reload handler.
 *
 * Symptom in Sentry (DEVOUR-WEB-2XQ): "TypeError: Importing a module script failed."
 * 258 events / 10 users in 5 days. Pattern: user has an old `index.html` cached
 * (stale chunk-hash references) and a new deploy invalidated those chunk files.
 * The dynamic `import()` for a lazy route can't resolve a chunk file that no
 * longer exists in the build artifacts. React surfaces the rejection as a fatal
 * uncaught error and the user sees a blank page.
 *
 * The fix is a forced hard reload — fetching `index.html` fresh pulls the new
 * chunk-hash references. We guard against an infinite reload loop with a
 * sessionStorage reload COUNTER capped at MAX_RELOADS: a stale-cache miss
 * recovers in one reload, so exceeding the cap means the build is genuinely
 * broken (network down, missing chunks) — stop reloading. The counter resets
 * only after the page runs STABLE_RESET_MS error-free (a real recovery), so a
 * fast re-failure loop can't reset it and slip past the cap.
 *
 * Wire from main.tsx (before React mounts) so the handler is registered before
 * any lazy chunk can fail.
 */

const RELOAD_COUNT_KEY = "devourplay_chunk_load_retry_count";

/*
 * Hard ceiling on forced reloads per session. A stale-cache miss recovers in a
 * single reload; needing more than this means the deployed build is genuinely
 * missing chunks, so we stop rather than storm the server / trap the user.
 */
const MAX_RELOADS = 2;

/*
 * How long the page must run WITHOUT another chunk failure before we consider the
 * reload a successful recovery and reset the counter (re-enabling recovery for a
 * later, unrelated stale chunk). A genuinely broken build re-fails well within
 * this window, so the counter survives the loop and MAX_RELOADS caps it.
 */
const STABLE_RESET_MS = 10000;

export function isChunkLoadFailure(err: unknown): boolean {
    if (!err) return false;
    const message = (err as Error)?.message ?? String(err);
    return (/Importing a module script failed/i).test(message) ||
        (/Failed to fetch dynamically imported module/i).test(message) ||
        (/ChunkLoadError/i).test(message) ||
        typeof message === "string" && message.includes("Loading chunk") && message.includes("failed");
}

export function attemptReload(): void {
    try {
        const count = parseInt(sessionStorage.getItem(RELOAD_COUNT_KEY) || "0", 10) || 0;
        if (count >= MAX_RELOADS) {
            /*
             * Already reloaded MAX_RELOADS times this session and STILL failing —
             * the build is genuinely broken (missing chunks), not a stale cache.
             * Stop so we don't reload-storm. The counter is reset by installChunkLoadRetry
             * only after the page runs STABLE_RESET_MS error-free, so a fast re-failure
             * loop never resets it and this ceiling holds.
             */
            return;
        }
        sessionStorage.setItem(RELOAD_COUNT_KEY, String(count + 1));
    } catch {
        /* sessionStorage may throw in private browsing — fall through and reload anyway */
    }
    /*
     * window.location.reload() honors the browser's HTTP cache, which is the very
     * thing that landed us here (stale index.html still in cache). Force-bust with
     * a query string instead so the server returns the current index.html.
     *
     * `new URL(window.location.href)` can throw if the page was navigated to an
     * opaque/unparseable URL (`javascript:`, certain `data:` payloads). We're
     * already inside an unhandledrejection/error handler — letting a nested
     * throw escape would itself become a Sentry event. Fall back to the cache-
     * honoring reload so the user at least gets a fresh page load attempt.
     */
    try {
        const url = new URL(window.location.href);
        url.searchParams.set("_cb", Date.now().toString());
        window.location.replace(url.toString());
    } catch {
        window.location.reload();
    }
}

export function installChunkLoadRetry(): void {
    /*
     * Reset the reload counter only AFTER the page has run STABLE_RESET_MS
     * error-free — i.e. we actually recovered. The previous design cleared the
     * guard immediately on the `load` event, which DEFEATED it: a React.lazy /
     * dynamic import() rejects asynchronously, AFTER `load` has already fired
     * (load does not wait on JS-initiated imports). So on a genuinely broken
     * build the guard was cleared before the same chunk re-failed, and every
     * reload triggered another reload — a storm. A delayed reset holds the
     * counter through a fast re-failure loop (capped by MAX_RELOADS in
     * attemptReload) while still re-enabling recovery for a later stale chunk.
     *
     * Scheduled on `load`, or immediately if we were wired after load already
     * fired (the timer, not the counter, is what gates recovery now).
     */
    const scheduleStableReset = (): void => {
        setTimeout(() => {
            try {
                sessionStorage.removeItem(RELOAD_COUNT_KEY);
            } catch { /* ignore */ }
        }, STABLE_RESET_MS);
    };
    if (document.readyState === "complete") {
        scheduleStableReset();
    } else {
        window.addEventListener("load", scheduleStableReset, {once: true});
    }

    window.addEventListener("error", (event) => {
        if (isChunkLoadFailure(event.error || event.message)) {
            attemptReload();
        }
    });

    window.addEventListener("unhandledrejection", (event) => {
        if (isChunkLoadFailure(event.reason)) {
            attemptReload();
        }
    });
}
